import type {
  ElementType,
  FunctionComponent,
  HTMLProps,
  KeyboardEvent,
  MouseEvent,
} from 'react'
import React, { useEffect, useState } from 'react'

import classnames from 'classnames'

import { TypographyV2 } from '../../../Seatbelt2/Foundations/Typography'
import styles from './AnswerRadio.module.scss'

export const AnswerRadio: FunctionComponent<AnswerRadioProps> = ({
  className,
  questionText,
  label,
  description,
  icon: Icon,
  priceBand,
  value,
  answerIndex,
  totalAnswers,
  selected = false,
  onClickCallback,
  ...rest
}) => {
  const hasIcon = !!Icon
  const [changeFilterMessage, setChangeFilterMessage] = useState<string>('')

  useEffect(() => {
    setTimeout(() => {
      setChangeFilterMessage('')
    }, 500)
  }, [changeFilterMessage])

  const escapedLabel = label.replaceAll("'", '')
  const inputId = `${questionText.replaceAll("'", '')}-${escapedLabel}`
  const ariaLabel = [
    label,
    description,
    `List item ${answerIndex + 1} of ${totalAnswers}`,
    `${selected ? '' : 'not '}selected`,
  ]
    .filter(Boolean)
    .join(', ')

  return (
    <div className={classnames(className, styles.answerRadio)}>
      <input
        className={styles.answerRadioInput}
        type="radio"
        id={inputId}
        name={questionText}
        value={value}
        checked={selected}
        tabIndex={0}
        readOnly={true}
      />
      <TypographyV2
        tag="label"
        textStyle="sb-text-interface-body-small-regular"
        className={styles.answerRadioLabel}
        aria-label={ariaLabel}
        htmlFor={inputId}
        tabIndex={0}
        onClick={(event: MouseEvent) => {
          setChangeFilterMessage(
            `${label} ${description ? description : ''} has been ${
              selected ? 'unselected' : 'selected'
            }`
          )
          onClickCallback && onClickCallback(event)
        }}
        onKeyPress={handleKeyPress}
        data-testid="answer-radio"
        {...rest}
      >
        <div aria-hidden={true}>
          {hasIcon && <Icon className={styles.answerRadioLabelIcon} />}
          <div className={styles.mainDescription}>
            <TypographyV2
              tag="span"
              textStyle="sb-text-body-default-strong"
              className={styles.answerRadioLabelText}
            >
              {label}
            </TypographyV2>
          </div>
          {description && (
            <TypographyV2
              tag="span"
              textStyle="sb-text-body-default-strong"
              className={styles.answerRadioLabelDescription}
            >
              {description}
            </TypographyV2>
          )}
          {priceBand && (
            <TypographyV2
              tag="p"
              textStyle="sb-text-interface-body-x-small-regular"
              className={styles.labelText}
            >
              ({'£'.repeat(priceBand)})
            </TypographyV2>
          )}
        </div>
      </TypographyV2>
      <div
        aria-live="polite"
        className={styles.offScreen}
        data-testid={`aria-live-${inputId}`}
      >
        {changeFilterMessage}
      </div>
    </div>
  )
}

///////// IMPLEMENTATION /////////

const isConfirmationKey = (key: string): boolean => [' ', 'Enter'].includes(key)

const handleKeyPress = (event: KeyboardEvent<HTMLLabelElement>) => {
  if (isConfirmationKey(event.key)) {
    const element = event.target as HTMLElement
    element.click()
  }
}

export type AnswerRadioProps = {
  /**
   * Optional class to add additional positioning and styling
   */
  className?: string
  /**
   * Question text that the radio button relates to
   */
  questionText: string
  /**
   * Index of current answer in answers array - for aria-label
   */
  answerIndex: number
  /**
   * Total number of answers related to the question (length of answers array) - for aria-label
   */
  totalAnswers: number
  /**
   * Optional callback that will be fired each time the radio button is clicked by the user
   * N.B First argument passed to callback will be an event
   */
  onClickCallback?: onClickCallback
  /**
   * Textfor answer to be displayed within the radio button label
   */
  label: string
  /**
   * Optional extra description of answer
   */
  description?: string
  /**
   * Name of icon to be displayed within the radio button label
   */
  icon: ElementType
  /**
   * Optional number indicating price band, displays as number of £ in brackets e.g 2-> (££)
   */
  priceBand?: number
  /**
   * Value of answer to be applied to radio button
   */
  value: string
  /**
   * Boolean dictating whether the radio button is currently selected
   */
  selected?: boolean
} & HTMLProps<HTMLLabelElement>

export type onClickCallback = (event?: MouseEvent | undefined) => void
